import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import Page from "@/components/layout/Page";
import { ProductModal } from "@/components/ProductModal";
import { ProductQuickView } from "@/components/ProductQuickView";
import { ProductImagePlaceholder } from "@/components/ProductImagePlaceholder";
import { products as localProducts, type Product } from "@/data/products";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";
import { useShopifyProducts, formatShopifyPrice } from "@/hooks/useShopifyProducts";
import { useCart } from "@/contexts/CartContext";
import { ShopifyProduct } from "@/lib/shopify";
import { Loader2 } from "lucide-react";

// Map Shopify handles to local product images
function getLocalImageForShopifyProduct(handle: string): { src: string; alt: string } | undefined {
  // Try to find matching local product by comparing handles/slugs
  const localProduct = localProducts.find(p => {
    const localHandle = p.slug.replace(/^\/product\//, '').replace(/^\//,'');
    return localHandle === handle || 
           p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') === handle ||
           handle.includes(localHandle) ||
           localHandle.includes(handle);
  });
  return localProduct?.image;
}

export default function Shop() {
  const { toast } = useToast();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const { addItem } = useCart();
  
  // Fetch products from Shopify
  const { data: shopifyProducts, isLoading, error } = useShopifyProducts(50);

  // Convert Shopify products to display format and sort by price
  const sortedProducts = useMemo(() => {
    if (!shopifyProducts || shopifyProducts.length === 0) return [];
    
    return [...shopifyProducts].sort((a, b) => {
      const priceA = parseFloat(a.node.priceRange.minVariantPrice.amount);
      const priceB = parseFloat(b.node.priceRange.minVariantPrice.amount);
      return priceB - priceA; // High to low
    });
  }, [shopifyProducts]);

  const handleAddToCart = (product: ShopifyProduct) => {
    const price = parseFloat(product.node.priceRange.minVariantPrice.amount);
    addItem({
      id: product.node.id,
      name: product.node.title,
      slug: `/product/${product.node.handle}`,
      price: price
    });
    toast({
      title: "Added to cart",
      description: `${product.node.title} has been added to your cart.`,
    });
  };

  const handleViewDetails = (product: ShopifyProduct) => {
    // Convert Shopify product to local Product format for modal
    const localImage = getLocalImageForShopifyProduct(product.node.handle);
    const localProduct = localProducts.find(p => 
      p.name.toLowerCase() === product.node.title.toLowerCase()
    );
    
    setSelectedProduct({
      name: product.node.title,
      slug: `/product/${product.node.handle}`,
      description: product.node.description || "Description coming soon",
      price: parseFloat(product.node.priceRange.minVariantPrice.amount),
      image: localImage || (product.node.images.edges[0] ? {
        src: product.node.images.edges[0].node.url,
        alt: product.node.images.edges[0].node.altText || product.node.title
      } : undefined),
      features: localProduct?.features
    });
  };
  
  return (
    <>
      <SEOHead
        title="Shop Sensory Tools"
        description="Shop premium tactile sensory boards, Library Squares, and Sensory Pads. Handmade in the UK for ADHD, autism, anxiety, and focus support."
        canonical="/shop"
        keywords="buy sensory boards, sensory tools shop, ADHD tools, autism sensory products, tactile learning tools, focus tools for office"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Shop", url: "/shop" }
      ]} />
      
      <Page>
      <section className="min-h-screen w-full">
        <div className="pb-24 flex flex-col gap-8">
        <header className="space-y-3 text-center md:text-left">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-emerald-300/80">
            Shop
          </p>
          <h1 className="text-3xl font-semibold tracking-tight page-title-gradient md:text-4xl">
            Choose the right Samboards tools for your space
          </h1>
          <p className="max-w-2xl text-sm text-slate-300/80">
            From full-size Samboards to portable Mini boards and specialist
            ranges, every product is designed to support autistic, ADHD and
            neurodivergent people in homes, schools and clinical settings.
          </p>
        </header>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-300" />
            <span className="ml-3 text-slate-300">Loading products...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-20">
            <p className="text-red-400">Failed to load products. Please try again later.</p>
          </div>
        )}

        {/* No Products State */}
        {!isLoading && !error && sortedProducts.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-800/50 border border-slate-700/50 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="text-xl font-semibold text-slate-200 mb-2">Products loading...</h3>
            <p className="text-slate-400 max-w-md mx-auto">
              Our product catalogue is syncing. Please refresh the page or check back shortly.
            </p>
          </div>
        )}

        {/* Products Grid */}
        {!isLoading && sortedProducts.length > 0 && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {sortedProducts.map((product) => {
              const localImage = getLocalImageForShopifyProduct(product.node.handle);
              const shopifyImage = product.node.images.edges[0]?.node;
              const displayImage = localImage || (shopifyImage ? { src: shopifyImage.url, alt: shopifyImage.altText || product.node.title } : null);
              const price = parseFloat(product.node.priceRange.minVariantPrice.amount);
              const isAvailable = product.node.variants.edges.some(v => v.node.availableForSale);
              
              // Find matching local product for features
              const localProduct = localProducts.find(p => 
                p.name.toLowerCase() === product.node.title.toLowerCase()
              );
              
              return (
                <article
                  key={product.node.id}
                  className="group relative flex flex-col h-full rounded-3xl border border-white/5 bg-slate-900/70 shadow-[0_18px_60px_rgba(0,0,0,0.65)] backdrop-blur-xl interactive-card"
                >
                  {/* Eco Badge for Agri products */}
                  {product.node.title.toLowerCase().includes('agri') && (
                    <span className="product-badge">
                      Eco-Friendly
                    </span>
                  )}
                  {!isAvailable && (
                    <span className="absolute top-3 left-3 z-20 inline-flex items-center rounded-full border border-slate-400/50 bg-slate-500/20 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.15em] text-slate-300 backdrop-blur-sm">
                      Out of stock
                    </span>
                  )}
                  
                  {/* Quick View Tooltip */}
                  {localProduct?.features && localProduct.features.length > 0 && (
                    <div className="absolute top-3 right-3 z-10 pointer-events-none">
                      <ProductQuickView
                        features={localProduct.features}
                        productName={product.node.title}
                      />
                    </div>
                  )}
                  
                  <div className="w-full h-[220px] md:h-[260px] lg:h-[300px] overflow-hidden rounded-t-3xl product-card-image-container">
                    {displayImage ? (
                      <div className="w-full h-full bg-gradient-to-br from-slate-800/60 to-slate-900/80 p-6">
                        <img 
                          src={displayImage.src} 
                          alt={displayImage.alt}
                          className="w-full h-full object-contain transition-transform duration-300"
                          style={{ 
                            imageRendering: '-webkit-optimize-contrast',
                            backfaceVisibility: 'hidden',
                            transform: 'translateZ(0)'
                          }}
                        />
                      </div>
                    ) : (
                      <ProductImagePlaceholder className="w-full h-full" aspectRatio="" />
                    )}
                  </div>
                  
                  <div className="flex flex-col flex-1 px-5 py-6">
                    <div className="space-y-3 flex-1">
                      <h2 className="text-xl font-semibold text-emerald-50 leading-tight">
                        {product.node.title}
                      </h2>
                      <p className={`text-sm leading-relaxed ${!product.node.description ? "text-slate-400/60 italic" : "text-slate-200/80"} line-clamp-3`}>
                        {product.node.description || "Description coming soon"}
                      </p>
                    </div>

                    <div className="mt-6 flex flex-col gap-3">
                      {price > 0 && (
                        <div className="flex items-baseline">
                          <span className="text-2xl font-bold text-emerald-300">
                            {formatShopifyPrice(product.node.priceRange.minVariantPrice.amount, product.node.priceRange.minVariantPrice.currencyCode)}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex flex-col xs:flex-row gap-2">
                        <Button
                          onClick={() => handleAddToCart(product)}
                          className="w-full rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 text-slate-900 font-medium shadow-[0_0_40px_rgba(45,255,196,0.35)] interactive-button disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none py-3 text-base"
                          disabled={!isAvailable}
                        >
                          {!isAvailable ? 'Out of stock' : 'Add to cart'}
                        </Button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleViewDetails(product);
                          }}
                          className="w-full rounded-full border-2 border-emerald-300/50 bg-transparent text-emerald-300 hover:bg-emerald-300/10 hover:text-emerald-200 hover:border-emerald-300 interactive-glow px-4 py-3 text-base font-semibold transition-all duration-200 cursor-pointer"
                        >
                          View details
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}

            {/* Access to Work CTA Card */}
            <article className="group relative flex flex-col h-full rounded-3xl border border-emerald-300/30 bg-gradient-to-br from-emerald-900/20 to-slate-900/70 shadow-[0_18px_60px_rgba(0,0,0,0.65)] backdrop-blur-xl interactive-card">
              <div className="flex flex-col flex-1 px-5 py-6">
                <div className="space-y-3 flex-1">
                  <span className="inline-flex items-center rounded-full border border-emerald-300/50 bg-emerald-300/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.15em] text-emerald-200">
                    Workplace Support
                  </span>
                  <h2 className="text-xl font-semibold text-emerald-50 leading-tight">
                    Access to Work Kits
                  </h2>
                  <p className="text-sm leading-relaxed text-slate-200/80">
                    Complete sensory support packages eligible for Access to Work funding. Perfect for neurodivergent employees seeking workplace adjustments.
                  </p>
                </div>

                <div className="mt-6">
                  <Link
                    to="/access-to-work-kits"
                    className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 px-4 py-2.5 text-sm font-medium text-slate-900 shadow-[0_0_40px_rgba(45,255,196,0.35)] interactive-button"
                  >
                    Learn more
                  </Link>
                </div>
              </div>
            </article>
          </div>
        )}
        </div>
      </section>

      <ProductModal
        isOpen={selectedProduct !== null}
        onClose={() => setSelectedProduct(null)}
        product={selectedProduct || {
          name: "",
          price: 0,
          description: ""
        }}
        onAddToCart={() => {
          if (selectedProduct) {
            // Find the Shopify product to add to cart
            const shopifyProduct = sortedProducts.find(
              p => p.node.title.toLowerCase() === selectedProduct.name.toLowerCase()
            );
            if (shopifyProduct) {
              handleAddToCart(shopifyProduct);
            }
          }
        }}
      />
    </Page>
    </>
  );
}
